import React, { useEffect, useState } from 'react';
import {
  Player,
  ChatMessage,
  Spectator,
  SocketCallback,
} from '../types';
import { SpectatePlayerInput } from './player-input/SpectatePlayerInput';
import { GameHeader } from './GameHeader';
import { QuestionAnswer } from './question-answer/QuestionAnswer';
import { PlayerInput } from './player-input/PlayerInput';
import { CorrectAnswer } from './CorrectAnswer';
import { ReportInaccuracyButton } from './ReportInaccuracyButton';
import { ScoreBar } from './score-bar';
import { Socket } from 'socket.io-client';
import {
  Stack,
  Box,
  Paper,
  Typography,
  Container,
  Avatar,
  Button,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider,
  ListItemAvatar,
  Drawer,
  useTheme,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import './styles.css';
import { RevealGuess } from './RevealGuess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { PoolPlayLeaderboard } from './PoolPlayLeaderboard';
import ChatIcon from '@mui/icons-material/Chat';
import { DraggableChat } from '../../../dialog/DraggableChat';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useNavigate } from 'react-router-dom';
import { SaveFlashcardButton } from './SaveFlashcardButton';
import { useReward } from 'react-rewards';
import { isMobile } from 'react-device-detect';
import { resizeImage } from '../../../api/cloudinary';
import CircleIcon from '@mui/icons-material/Circle';
import { FrameRate } from './FrameRate';
import { Latency } from './Latency';
import PauseIcon from '@mui/icons-material/Pause';
import { CustomQuestionTestingData } from '../../custom-questions';
import {
  Penalty,
  ProperNameShortAnswer,
  Team,
  Teams,
} from '@par/shared/types/game';
import { ChallengeTowerBar } from './ChallengeTowerBar';
import { ChallengeLifes } from './ChallengeLifes';
import { findMark } from '../utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { StarterKitConfig } from '../../starter-kits/types';
import StopIcon from '@mui/icons-material/Stop';
import { AbortStarterKitDialog } from '../../../dialog/AbortStarterKitDialog';
import { TournamentSitAndGoLeaderboard } from './TournamentSitAndGoLeaderboard';
import { sortPlayers } from '..';
import { Assists, Mode } from '@par/shared/common/types';
import { TeamInputLive } from './TeamInputLive';
import HandshakeIcon from '@mui/icons-material/Handshake';

export const poolLeaderboardDrawerWidth = 300;
export const poolLeaderboardDrawerWidthMobile = 144;
export const challengeTowerStages = [
  {
    value: 0,
    lastValueInStage: null,
    label: 'Normal',
    color: '#ccf49f',
    textColor: 'black',
    // description: 'Play regular PAR',
    stage: 1,
  },
  {
    value: 10,
    lastValueInStage: 9,
    label: '1 Eagle',
    color: 'rgb(67, 181, 196)',
    textColor: 'black',
    description: 'Achieve 1 Eagle',
    challengeType: 'active',
    stage: 2,
  },
  {
    value: 20,
    lastValueInStage: 19,
    label: '1 Condor',
    color: 'rgb(200, 110, 0)',
    textColor: 'white',
    description: 'Achieve 1 Condor',
    challengeType: 'active',
    stage: 3,
  },

  {
    value: 30,
    lastValueInStage: 29,
    label: '19 Seconds',
    color: 'rgb(251, 113, 133)',
    textColor: 'black',
    description: 'Answer within 19 seconds',
    timerLimit: 19,
    challengeType: 'passive',
    stage: 5,
  },
  {
    value: 40,
    lastValueInStage: 39,
    label: 'Normal',
    color: '#4f4f4f',
    textColor: 'white',
    description: 'Take a breather and play normally',
    challengeType: 'passive',
    stage: 6,
  },
  {
    value: 50,
    lastValueInStage: 49,
    label: '1 Pterodactyl',
    color: 'rgb(175, 164, 41)',
    textColor: 'black',
    description: 'Achieve 1 Pterodactyl',
    challengeType: 'active',
    stage: 4,
  },

  {
    value: 60,
    lastValueInStage: 59,
    label: '18 Seconds',
    color: 'rgb(244, 63, 94)',
    textColor: 'white',
    timerLimit: 18,
    description: 'Answer within 18 seconds',
    challengeType: 'passive',
    stage: 7,
  },
  {
    value: 70,
    lastValueInStage: 69,
    label: 'No Fuzzy',
    color: 'rgb(255, 0, 255)',
    textColor: 'white',
    description: 'Perfect spelling',
    challengeType: 'passive',
    stage: 8,
  },
  {
    value: 80,
    lastValueInStage: 79,
    label: 'No Letters',
    color: 'rgb(255, 255, 255)',
    textColor: 'black',
    description: 'No letters',
    challengeType: 'passive',
    stage: 9,
  },
  {
    value: 90,
    lastValueInStage: 89,
    label: 'No Blanks',
    color: '#e5de56',
    textColor: 'black',
    description: 'No letters and no blanks',
    challengeType: 'passive',
    stage: 10,
  },
  {
    value: 100,
    lastValueInStage: 99,
    label: 'The Spire',
    color: '#ecbfa1',
    textColor: 'black',
    description:
      'No letters, no blanks, no typos, and answer within 18 seconds',
    challengeType: 'passive',
    stage: 11,
  },
];

let displayGameClockTimeout;

type PropTypes = {
  answer?: string[][];
  assists?: Assists;
  bannedCategories?: string[];
  category: string;
  categorySelections?: string[];
  categorySet: string;
  clearAfterPenalty?: boolean;
  customQuestionTestingData?: CustomQuestionTestingData;
  cutoff?: number;
  daily?: boolean;
  dailyChallengeTower?: number;
  dailyTheme?: string;
  delayBeforeQuestionRevealCount: number;
  demo?: boolean;
  demons?: boolean;
  displaySize: 'sm' | 'md' | 'lg';
  fastNext: boolean;
  freezeGameClock: boolean;
  fullQuestion?: string;
  fuzzy?: Record<string, number>;
  gameClock: number;
  gameId: string;
  gameOver: boolean;
  gameType: 'casual' | 'pro';
  ghostChallenge?: boolean;
  ghostChallengeFrom?: string;
  ghostChallengeTo?: string;
  handleDisplaySizeChange: (e: any, size: 'sm' | 'md' | 'lg') => void;
  handlePause: () => void;
  isAdmin: boolean;
  league?: boolean;
  lifes?: number;
  liveInputs?: Record<string, string>;
  matchMaking?: boolean;
  matchMakingSeededBy?: string;
  mathFormula?: string;
  mode: Mode;
  myTeam?: Team;
  nextQuestionDelayCount: number;
  otherTeam?: Team;
  par: number;
  paused: boolean;
  penalty?: Penalty;
  playerSelf: Player;
  players: Record<string, Player>;
  playerThem?: Player;
  profileId: string;
  properNameShortAnswer?: ProperNameShortAnswer;
  question: string;
  questionImgUrl?: string;
  questionIndex: number;
  ranked?: boolean;
  rejoin: boolean;
  roundOver: boolean;
  showConnectionStats?: boolean;
  showSnackbarMessage: (message: string) => void;
  socket?: Socket;
  spectateSocket?: Socket;
  spectate: boolean;
  spectateGameId?: string;
  spectatorCount: number;
  spectatorList: Spectator[];
  starterKit?: StarterKitConfig;
  stopSpectating: (isPopState: boolean) => void;
  submittedByUsername?: string;
  teams: Teams;
  timerLimitReached: boolean;
  tournamentTotalPlayers?: number;
  totalNumQuestions: number | undefined;
  tournament?: number;
  survivalChallengeStatuses?: {
    firstChallengeMet: boolean;
    secondChallengeMet: boolean;
    thirdChallengeMet: boolean;
  };
  survivalMode?: 'endurance' | 'challenge' | 'demon';
  unrevealedIndex?: number[];
  username: string;
  variant: 'normal' | 'survival' | 'starter-kit' | 'starter-kit' | 'turbo';
  willPause: boolean;
};

export const Game = ({
  answer,
  assists,
  bannedCategories,
  cutoff,
  daily,
  dailyChallengeTower,
  dailyTheme,
  category,
  categorySelections,
  categorySet,
  clearAfterPenalty,
  customQuestionTestingData,
  delayBeforeQuestionRevealCount,
  demo,
  demons,
  displaySize,
  fastNext,
  freezeGameClock,
  fullQuestion,
  fuzzy,
  gameClock,
  gameId,
  gameOver,
  gameType,
  ghostChallenge,
  ghostChallengeFrom,
  ghostChallengeTo,
  handleDisplaySizeChange,
  handlePause,
  isAdmin,
  league,
  lifes,
  liveInputs,
  matchMaking,
  matchMakingSeededBy,
  mathFormula,
  mode,
  myTeam,
  nextQuestionDelayCount,
  otherTeam,
  par,
  paused,
  penalty,
  playerSelf,
  players,
  playerThem,
  profileId,
  properNameShortAnswer,
  question,
  questionImgUrl,
  questionIndex,
  ranked,
  rejoin,
  roundOver,
  showConnectionStats,
  showSnackbarMessage,
  socket,
  spectateSocket,
  spectate,
  spectateGameId,
  spectatorCount,
  spectatorList,
  starterKit,
  stopSpectating,
  submittedByUsername,
  survivalChallengeStatuses,
  teams,
  timerLimitReached,
  tournamentTotalPlayers,
  totalNumQuestions,
  tournament,
  survivalMode,
  unrevealedIndex,
  username,
  variant,
  willPause,
}: PropTypes) => {
  const navigate = useNavigate();
  const [sortedPlayers, setSortedPlayers] = useState<Player[]>();
  const [playerThemGuess, setPlayerThemGuess] = useState<string>();
  const [displayGameClock, setDisplayGameClock] = useState(false);
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>(
    mode === 'match' || mode === 'solo'
      ? Object.values(players).map((p) => p.profileId)
      : mode === 'sit-n-go'
      ? Object.values(players)
          .slice(0, 8)
          .map((p) => p.profileId)
      : []
  );
  const [spectatorChat, setSpectatorChat] = useState<ChatMessage[]>();
  const [openSpectatorChat, setOpenSpectatorChat] = useState(false);
  const [spectatorChatLastSeen, setSpectatorChatLastSeen] = useState<number>();
  const [unreadSpectatorChat, setUnreadSpectatorChat] = useState<boolean>();
  const [wonRiddlerChallenge, setWonRiddlerChallenge] = useState(false);
  const { reward: confettiReward } = useReward('confettiReward', 'confetti');
  const { reward: balloonsReward } = useReward('balloonsReward', 'balloons');
  const [focusInput, setFocusInput] = useState(false);

  const [currentLifes, setCurrentLifes] = useState<number>(1);
  const [showHeart, setShowHeart] = useState(false);
  const [showUsedHeart, setShowUsedHeart] = useState(false);
  const [showUsedHeartOnChallenge, setShowUsedHeartOnChallenge] = useState<
    'timeout' | 'non-timeout'
  >();
  const [showAbortConfirmDialog, setShowAbortConfirmDialog] = useState(false);
  const [connectCounter, setConnectCounter] = useState(0);
  const [disconnectCounter, setDisconnectCounter] = useState(0);

  const handleConnect = () => {
    setConnectCounter((prev) => prev + 1);
  };

  const handleDisconnect = () => {
    setDisconnectCounter((prev) => prev + 1);
  };

  useEffect(() => {
    if (!socket) return;
    // every component can have a socket listener like these connects and disconnects
    //  so that component specific states can be handled in isolation
    // the counters are a clean workaround to handle updating states since the listeners
    //  contain the intial states on mount
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, []);

  useEffect(() => {
    setDisplayGameClock(true);
  }, [connectCounter]);

  useEffect(() => {
    clearTimeout(displayGameClockTimeout);
  }, [disconnectCounter]);

  useEffect(() => {
    const sortedPlayers = sortPlayers(players);
    setSortedPlayers(sortedPlayers);
  }, [players]);

  useEffect(() => {
    if (!socket) return;
    socket.on('riddler', () => {
      confettiReward();
      balloonsReward();
      setWonRiddlerChallenge(true);
    });
    const keyDownHandler = (event: any) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCloseSpectatorChat();
        abortStarterKitGame();
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        handleOpenSpectatorChat();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
      socket.off('riddler');
    };
  }, [socket]);

  useEffect(() => {
    if (lifes === undefined) return;
    if (lifes > currentLifes) {
      setShowHeart(true);
      setTimeout(() => {
        setShowHeart(false);
      }, 3000);
    }
    if (lifes < currentLifes) {
      setShowUsedHeart(true);
      setTimeout(() => {
        setShowUsedHeart(false);
      }, 3000);
    }
  }, [lifes]);

  useEffect(() => {
    if (!penalty || !playerThem) return;
    setPlayerThemGuess(
      penalty.profileId === playerThem.profileId
        ? penalty?.revealedAnswer
        : undefined
    );
  }, [penalty]);

  useEffect(() => {
    setCurrentLifes(lifes);
  }, [lifes]);

  useEffect(() => {
    if (!showUsedHeart) return;
    if (isChallengeMet() === 'no') {
      setShowUsedHeartOnChallenge('non-timeout');
      return;
    }
    if (!playerSelf?.correctAnswer && playerSelf?.timerLimitReached) {
      setShowUsedHeartOnChallenge('timeout');
      return;
    }
  }, [showUsedHeart, playerSelf]);

  useEffect(() => {
    // this is not pretty, could probably handle this better from the serve
    if (
      nextQuestionDelayCount <= 3 &&
      (timerLimitReached || playerSelf?.correctAnswer)
    ) {
      clearTimeout(displayGameClockTimeout);
      displayGameClockTimeout = setTimeout(() => {
        setDisplayGameClock(false);
      }, 200);
      return;
    }
    if (
      delayBeforeQuestionRevealCount === 1 &&
      !timerLimitReached &&
      !playerSelf?.correctAnswer
    ) {
      setDisplayGameClock(false);
      return;
    }
    if (delayBeforeQuestionRevealCount <= 0) {
      setDisplayGameClock(true);
    }
  }, [
    delayBeforeQuestionRevealCount,
    nextQuestionDelayCount,
    timerLimitReached,
    playerSelf,
  ]);

  useEffect(() => {
    if (!spectate) return;
    setDisplayGameClock(true);
    socket?.on('spectatorChat', reverseSpectatorChat);
  }, [spectate]);

  useEffect(() => {
    if (!rejoin) return;
    setDisplayGameClock(true);
  }, [rejoin]);

  useEffect(() => {
    if (!spectatorChat) return;
    const recentMessageCreatedAt =
      spectatorChat[spectatorChat.length - 1]?.createdAt || 0;
    if (
      !openSpectatorChat &&
      recentMessageCreatedAt > (spectatorChatLastSeen || 0)
    ) {
      setUnreadSpectatorChat(true);
    }
  }, [spectatorChat]);

  const theme = useTheme();

  const playerMeAnsweredFirst = () => {
    const playerMeScore = playerSelf?.currentScore || 0;
    if (playerMeScore !== undefined && mode === 'sit-n-go') return true;
    const playerThemScore = playerThem?.currentScore || 0;
    return (
      // BUG: If both players have score 0 and then playerMe answers on par and gets score 0 this will return false, but it should return true and show the opponent's timer
      (!!playerMeScore && !playerThemScore) || playerMeScore < playerThemScore
    );
  };

  const teamAnsweredFirst = (team: 'teamA' | 'teamB') => {
    if (!teams) return false;
    const teamScore = teams[team].currentScore;
    const otherTeam = team === 'teamA' ? 'teamB' : 'teamA';
    const otherTeamScore = teams[otherTeam].currentScore;
    return teamScore !== null && otherTeamScore === null;
  };

  const displayOpponentTimer = () => {
    if (mode === 'sit-n-go')
      return freezeGameClock && playerMeAnsweredFirst() && !spectate;
    if (mode === 'teams' && teams && myTeam) {
      return freezeGameClock && teamAnsweredFirst(myTeam.id) && !spectate;
    }
    return (
      freezeGameClock && playerMeAnsweredFirst() && playerThem && !spectate
    );
  };

  const handlePoolLeaderboardUsernameClick = (profileId: string) => {
    if (spectate && !selectedProfileIds.includes(profileId)) {
      if (selectedProfileIds.length < 8) {
        setSelectedProfileIds((prev) => prev.concat(profileId));
      } else {
        setSelectedProfileIds((prev) => prev.slice(0, 7).concat(profileId));
      }
    }
  };

  const deselectPlayer = (profileId: string) => {
    if (spectate) {
      const updated = selectedProfileIds.filter((pid) => pid !== profileId);
      setSelectedProfileIds(updated);
    }
  };

  const handleSelectedProfileIdsFilter = (
    filter: 'removeAll' | 'top4' | 'top8' | '5to12'
  ) => {
    if (!sortedPlayers) return;
    if (filter === 'removeAll') {
      setSelectedProfileIds([]);
      return;
    }
    if (filter === 'top4') {
      const top4 = sortedPlayers.slice(0, 4).map((p) => p.profileId);
      setSelectedProfileIds(top4);
      return;
    }
    if (filter === 'top8') {
      const top8 = sortedPlayers.slice(0, 8).map((p) => p.profileId);
      setSelectedProfileIds(top8);
      return;
    }
    if (filter === '5to12') {
      const fiveToTwelve = sortedPlayers.slice(4, 12).map((p) => p.profileId);
      setSelectedProfileIds(fiveToTwelve);
      return;
    }
  };

  const handleOpenSpectatorChat = () => {
    if (!spectate) return;
    setOpenSpectatorChat(true);
    setUnreadSpectatorChat(false);
  };

  const handleCloseSpectatorChat = () => {
    if (!spectate) return;
    setOpenSpectatorChat(false);
    if (!spectatorChat) return;
    setSpectatorChatLastSeen(
      spectatorChat[spectatorChat.length - 1]?.createdAt
    );
  };

  const reverseSpectatorChat = (spectatorChat: ChatMessage[]) => {
    const rev = spectatorChat.sort(
      (a, b) => (a.createdAt || 0) - (b.createdAt || 0)
    );
    setSpectatorChat(rev);
  };

  const handleExitSpectate = () => {
    stopSpectating(false);
    navigate('/lobby');
  };

  const scoreBars = () => {
    if (!playerSelf) return <></>;
    return (
      <>
        <ScoreBar
          avatar={playerSelf.avatarUrl}
          mode={mode}
          score={playerSelf.parScore}
          username={playerSelf.username}
          usernameLocation={'left'}
        ></ScoreBar>
        {mode === 'match' && playerThem ? (
          <>
            <ScoreBar
              avatar={playerThem.avatarUrl}
              mode={mode}
              score={playerThem.parScore}
              username={playerThem.username}
              usernameLocation={'right'}
            ></ScoreBar>
          </>
        ) : null}
      </>
    );
  };

  const teamScoreBars = () => {
    return (
      <>
        <ScoreBar
          avatar={''}
          mode={mode}
          score={myTeam?.parScore || 0}
          username={myTeam?.username}
          usernameLocation={'left'}
        />
        <ScoreBar
          avatar={''}
          mode={mode}
          score={otherTeam?.parScore || 0}
          username={otherTeam?.username}
          usernameLocation={'right'}
        />
      </>
    );
  };

  // const birdAnimation = player.roundScore[questionIndex]?.score <= 0 ? (
  //   <motion.div
  //     variants={animationVariants}
  //     animate={'move'}
  //     className="bird-cont"
  //   >
  //     <img className="bird-pic" src={'Test-Eagle.jpg'} alt="ape1"></img>
  //   </motion.div>
  // ) : null

  const spectatorsTooltip = (
    <>
      <Typography variant="h6">Spectators</Typography>
      <Divider />
      {tournament ? (
        <Stack mt={2}>
          {spectatorList?.map((s) => (
            <Typography sx={{ color: theme.palette.dailies.main }}>
              {s.username}
            </Typography>
          ))}
        </Stack>
      ) : (
        <List
          sx={{
            bgcolor: 'background.paper',
            position: 'relative',
            overflow: 'auto',
            maxHeight: 300,
          }}
        >
          {spectatorList?.map((s) => (
            <ListItem>
              <ListItemAvatar>
                <Avatar
                  alt={s.username}
                  src={resizeImage(s.avatarPublicId, 50, 50)}
                />
              </ListItemAvatar>
              <ListItemText primary={s.username} />
            </ListItem>
          ))}
        </List>
      )}
    </>
  );

  const spectatorChatTooltip = (
    <Stack>
      <Typography variant="body1">[Enter] open chat</Typography>
      <Typography variant="body1">[ESC] close chat</Typography>
    </Stack>
  );

  const exitSpectateTooltip = (
    <Typography variant="body1">Leave spectate</Typography>
  );

  const handlePauseClick = () => {
    setFocusInput((prev) => !prev);
    if (mode !== 'solo' && !isAdmin) return;
    handlePause();
  };

  const isChallengeMet = () => {
    let index = -1;
    for (let i = challengeTowerStages.length - 1; i >= 0; i--) {
      if (questionIndex + 1 > challengeTowerStages[i].value) {
        index = i;
        break;
      }
    }
    const isLastQuestionInStage =
      questionIndex === challengeTowerStages[index]?.lastValueInStage;
    if (index === 1) {
      if (survivalChallengeStatuses?.firstChallengeMet) {
        return 'yes';
      }
      if (!playerSelf?.correctAnswer && !playerSelf?.timerLimitReached)
        return 'tbd';
      if (
        isLastQuestionInStage &&
        !survivalChallengeStatuses?.firstChallengeMet
      ) {
        return 'no';
      }
    }
    if (index === 2) {
      if (survivalChallengeStatuses?.secondChallengeMet) {
        return 'yes';
      }
      if (!playerSelf?.correctAnswer && !playerSelf?.timerLimitReached)
        return 'tbd';
      if (
        isLastQuestionInStage &&
        !survivalChallengeStatuses?.secondChallengeMet
      ) {
        return 'no';
      }
    }
    if (index === 5) {
      if (survivalChallengeStatuses?.thirdChallengeMet) {
        return 'yes';
      }
      if (!playerSelf?.correctAnswer && !playerSelf?.timerLimitReached)
        return 'tbd';
      if (
        isLastQuestionInStage &&
        !survivalChallengeStatuses?.thirdChallengeMet
      ) {
        return 'no';
      }
    }
    return 'tbd';
  };
  const abortStarterKitGame = () => {
    if (mode !== 'solo' || !starterKit) return;
    setShowAbortConfirmDialog(true);
  };

  const abortStarterKitGameConfirmed = () => {
    if (mode !== 'solo' || !starterKit) return;
    socket.emit(
      'abortStarterKitGame',
      {
        profileId: profileId,
        gameId: gameId,
      },
      (res: SocketCallback) => {
        if (res.status === 'ok') {
          showSnackbarMessage('Game aborted');
          navigate('/explore/starter-kits');
        }
      }
    );
  };

  const restartStarterKitGameConfirmed = () => {
    if (mode !== 'solo' || !starterKit) return;
    socket.emit(
      'restartStarterKitGame',
      {
        profileId: profileId,
        gameId: gameId,
      },
      (res: SocketCallback) => {
        if (res.status === 'ok') {
          setShowAbortConfirmDialog(false);
        }
      }
    );
  };

  return (
    <>
      <AbortStarterKitDialog
        close={() => setShowAbortConfirmDialog(false)}
        confirmAbort={abortStarterKitGameConfirmed}
        confirmRestart={restartStarterKitGameConfirmed}
        open={showAbortConfirmDialog}
      />
      <DraggableChat
        chat={spectatorChat}
        closeChat={handleCloseSpectatorChat}
        gameId={spectateGameId}
        openChat={openSpectatorChat}
        profileId={profileId}
        socket={socket}
        socketEmitEvent={'spectatorChat'}
        title="SPECTATOR CHAT"
        username={username}
      />
      <Box>
        {showConnectionStats && !gameOver && !customQuestionTestingData ? (
          <Stack
            sx={{
              position: 'absolute',
              top: '10px',
              right:
                mode === 'sit-n-go' ||
                (variant === 'survival' && survivalMode === 'challenge')
                  ? `${poolLeaderboardDrawerWidth + 10}px`
                  : '10px',
            }}
          >
            <Stack direction={'row'} spacing={2}>
              {!socket?.connected ? (
                <Typography variant={'caption'} color="warning.main">
                  Reconnecting...
                </Typography>
              ) : (
                <Typography variant={'caption'} color="success.main">
                  Connected:
                </Typography>
              )}
              <Tooltip
                title={`Connection status: ${
                  socket?.connected
                    ? 'connected'
                    : 'disconnected'
                }`}
                placement="bottom-start"
              >
                <Box>
                  <CircleIcon
                    sx={{
                      fontSize: '16px',
                      color:
                        socket?.connected
                          ? 'green'
                          : 'red',
                    }}
                  />
                </Box>
              </Tooltip>
            </Stack>
            <FrameRate></FrameRate>
            <Latency socket={socket} profileId={profileId}></Latency>
            <Typography variant={'caption'} textAlign={'right'}>
              Disconnects: {disconnectCounter}
            </Typography>
          </Stack>
        ) : null}
        <Box sx={{ position: 'absolute', top: 140, right: 0, zIndex: 999 }}>
          <ToggleButtonGroup
            value={displaySize}
            exclusive
            onChange={handleDisplaySizeChange}
            size="small"
          >
            <ToggleButton value="sm">sm</ToggleButton>
            <ToggleButton value="md">md</ToggleButton>
            <ToggleButton value="lg">lg</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box
          sx={{
            width: {
              xs:
                mode === 'pool' ||
                mode === 'sit-n-go' ||
                (variant === 'survival' && survivalMode === 'challenge')
                  ? `calc(100% - ${poolLeaderboardDrawerWidthMobile}px)`
                  : '100%',
              sm:
                mode === 'pool' ||
                mode === 'sit-n-go' ||
                (variant === 'survival' && survivalMode === 'challenge')
                  ? `calc(100% - ${poolLeaderboardDrawerWidth}px)`
                  : '100%',
            },
          }}
        >
          {/* <div className="score-bar-username">{playerMe.username}</div> */}
          <Container
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: theme.palette.background.default + 'e6',
            }}
          >
            {spectate && mode === 'sit-n-go'
              ? null
              : mode === 'teams'
              ? teamScoreBars()
              : scoreBars()}
            <Stack spacing={1}>
              <GameHeader
                bannedCategories={bannedCategories}
                category={category}
                categorySelections={categorySelections}
                categorySet={categorySet}
                challengeTowerAltTimeout={
                  findMark(questionIndex + 1, challengeTowerStages).timerLimit
                }
                customQuestionTestingData={customQuestionTestingData}
                daily={daily}
                dailyChallengeTower={dailyChallengeTower}
                dailyTheme={dailyTheme}
                delayBeforeQuestionRevealCount={delayBeforeQuestionRevealCount}
                demo={demo}
                demons={demons}
                displayGameClock={displayGameClock}
                fastNext={fastNext}
                freezeGameClock={freezeGameClock}
                gameClock={gameClock}
                gameType={gameType}
                ghostChallenge={ghostChallenge}
                ghostChallengeFrom={ghostChallengeFrom}
                ghostChallengeTo={ghostChallengeTo}
                goToNextQuestionEarly={!!fullQuestion} // if fullQuestion was received, that means all players answered correctly and will now move to the next question before the 20 sec mark
                league={league}
                lifes={lifes}
                matchMaking={matchMaking}
                matchMakingSeededBy={matchMakingSeededBy}
                mode={mode}
                myTeam={myTeam}
                nextQuestionDelayCount={nextQuestionDelayCount}
                otherTeam={otherTeam}
                par={par}
                penalty={penalty}
                players={players}
                playerSelf={playerSelf}
                playerThem={playerThem}
                questionIndex={questionIndex}
                starterKit={starterKit}
                submittedByUsername={submittedByUsername}
                ranked={ranked}
                roundOver={roundOver}
                timerLimitReached={timerLimitReached}
                totalNumQuestions={totalNumQuestions}
                tournament={tournament}
                showHeart={showHeart}
                showUsedHeart={showUsedHeart}
                socket={socket}
                spectate={spectate}
                survivalMode={survivalMode}
                variant={variant}
              />
              <Typography
                className={`opponent-timer ${
                  displayOpponentTimer() ? 'fade-in' : ''
                }`}
                sx={{
                  visibility: displayOpponentTimer() ? 'visible' : 'hidden',
                }}
              >
                Opponent's timer 00:{gameClock < 10 ? '0' : null}
                {gameClock || 0}
              </Typography>
              <RevealGuess
                avatarPublicId={playerThem?.avatarPublicId}
                playerThemGuess={playerThemGuess}
              ></RevealGuess>
            </Stack>
          </Container>
          <Container
            sx={{
              height:
                displaySize === 'sm'
                  ? '220px'
                  : displaySize === 'md'
                  ? '260px'
                  : displaySize === 'lg' && league
                  ? '340px'
                  : '300px',
            }}
          >
            <QuestionAnswer
              answer={answer}
              correctAnswer={playerSelf?.correctAnswer || myTeam?.correctAnswer}
              displaySize={displaySize}
              fullQuestion={fullQuestion}
              mathFormula={mathFormula}
              mode={mode}
              properNameShortAnswer={properNameShortAnswer}
              question={question}
              questionImgUrl={questionImgUrl}
              questionIndex={questionIndex}
              spectate={spectate}
              timerLimitReached={timerLimitReached}
              unrevealedIndex={unrevealedIndex}
            ></QuestionAnswer>
          </Container>
          {/* {isAdmin && !isMobile && !customQuestionTestingData ? (
            <Box
              sx={{
                width: 'fit-content',
                marginTop: '-50px',
                right: 0,
                position: 'fixed',
                zIndex: 100,
              }}
            >
              <SaveFlashcardButton
                profileId={profileId}
                questionIndex={questionIndex}
                gameId={gameId}
                socket={socket}
              ></SaveFlashcardButton>
            </Box>
          ) : null} */}
          <Stack
            spacing={1}
            sx={{
              width: 'fit-content',
              marginTop: '-140px',
              position: 'fixed',
              zIndex: 100,
            }}
          >
            {spectate && (spectatorList?.length > 0 || spectatorCount > 0) ? (
              <Tooltip title={exitSpectateTooltip} placement="right">
                <Button
                  variant="outlined"
                  startIcon={<ExitToAppIcon />}
                  onClick={handleExitSpectate}
                ></Button>
              </Tooltip>
            ) : null}
            {spectatorList?.length > 0 || spectatorCount > 0 ? (
              <Tooltip
                title={spectatorList?.length ? spectatorsTooltip : null}
                placement="right"
              >
                <Button variant="outlined" startIcon={<VisibilityIcon />}>
                  {spectatorList?.length || spectatorCount}
                </Button>
              </Tooltip>
            ) : null}
            {spectate && (spectatorList?.length > 0 || spectatorCount > 1) ? (
              <Tooltip title={spectatorChatTooltip} placement="right">
                <Button
                  variant="outlined"
                  startIcon={
                    unreadSpectatorChat ? (
                      <MarkUnreadChatAltIcon sx={{ color: 'info.main' }} />
                    ) : (
                      <ChatIcon
                        sx={{
                          color: 'primary.main',
                        }}
                      />
                    )
                  }
                  onClick={handleOpenSpectatorChat}
                ></Button>
              </Tooltip>
            ) : null}
          </Stack>
          {/* {wonRiddlerChallenge ? (
            <Typography
              variant="h5"
              textAlign={'center'}
              sx={{
                color: theme.palette.underPar.main,
                border: '1px solid',
                borderColor: theme.palette.underPar.main,
                borderRadius: '.25rem',
              }}
            >
              CONGRATS! YOU WON THE RIDDLER..ER CHALLENGE. LET THE ADMINS KNOW
              IN THE DISCORD!
            </Typography>
          ) : null} */}
          {willPause ? (
            <Typography
              variant="h6"
              textAlign={'center'}
              sx={{
                border: '1px solid',
                borderColor: theme.palette.info.main,
                borderRadius: '.25rem',
              }}
            >
              Game will PAUSE before the start of the next question
            </Typography>
          ) : null}
          <Paper
            elevation={6}
            sx={{
              height: {
                xs: 300,
                sm: `${
                  spectate && (mode === 'sit-n-go' || mode === 'teams')
                    ? '18rem'
                    : mode === 'teams'
                    ? '12rem'
                    : '8rem'
                }`,
              },
              width: { xs: '100vw', sm: '100%' },
              position: 'sticky',
              bottom: 0,
              zIndex: { xs: '10', sm: null },
            }}
          >
            {spectate && mode === 'sit-n-go' && sortedPlayers ? (
              <Stack
                direction={'row'}
                spacing={2}
                sx={{ position: 'absolute' }}
              >
                <Button
                  variant="text"
                  onClick={() => handleSelectedProfileIdsFilter('removeAll')}
                >
                  REMOVE ALL
                </Button>
                <Button
                  variant="text"
                  onClick={() => handleSelectedProfileIdsFilter('top4')}
                >
                  TOP 4
                </Button>
                {sortedPlayers.length > 4 ? (
                  <Button
                    variant="text"
                    onClick={() => handleSelectedProfileIdsFilter('top8')}
                  >
                    TOP 8
                  </Button>
                ) : null}
              </Stack>
            ) : null}
            <Stack
              direction={'row'}
              width={'100%'}
              height={'100%'}
              justifyContent={'center'}
              alignItems={'center'}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                }}
              >
                {!spectate && myTeam ? (
                  <TeamInputLive
                    admin={isAdmin}
                    assists={assists}
                    fuzzy={fuzzy}
                    gameId={gameId}
                    league={league}
                    mode={mode}
                    myTeam={myTeam}
                    penalty={penalty}
                    players={players}
                    profileId={profileId}
                    questionIndex={questionIndex}
                    socket={socket}
                    spectate={spectate}
                    timerLimitReached={timerLimitReached}
                    username={username}
                  />
                ) : null}
              </Box>
              <Stack
                spacing={0}
                alignItems="center"
                justifyContent="center"
                width={'100%'}
                height={'100%'}
              >
                {!spectate ? (
                  <Stack position="relative" justifyContent={'center'}>
                    {assists && assists.assisterProfileId === profileId ? (
                      <Box
                        width={'100%'}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                        }}
                      >
                        <Box className="float-fade">
                          <Stack
                            direction={'row'}
                            spacing={2}
                            alignItems={'center'}
                            justifyContent={'center'}
                          >
                            <HandshakeIcon
                              style={{
                                color: theme.palette.assist.main,
                              }}
                            />
                            <Typography
                              variant="h6"
                              sx={{
                                color: theme.palette.assist.main,
                              }}
                              textAlign={'center'}
                              fontWeight={600}
                            >
                              Nice assist!
                            </Typography>
                          </Stack>
                        </Box>
                      </Box>
                    ) : null}
                    <PlayerInput
                      answer={answer}
                      category={category}
                      clearAfterPenalty={clearAfterPenalty}
                      correctAnswer={playerSelf?.correctAnswer}
                      fuzzy={fuzzy}
                      focusInput={focusInput}
                      gameId={gameId}
                      mode={mode}
                      myTeam={myTeam}
                      onPause={handlePause}
                      penalty={penalty}
                      playerSelf={playerSelf}
                      questionIndex={questionIndex}
                      showPreviewModeMessage={!!customQuestionTestingData}
                      socket={socket}
                      spectate={spectate}
                      teams={teams}
                      timerLimitReached={timerLimitReached}
                    ></PlayerInput>
                    <CorrectAnswer
                      mode={mode}
                      player={playerSelf}
                      myTeam={myTeam}
                    />
                  </Stack>
                ) : (
                  <SpectatePlayerInput
                    answer={answer}
                    deselectPlayer={deselectPlayer}
                    fuzzy={fuzzy}
                    isAdmin={isAdmin}
                    league={league}
                    liveInputs={liveInputs}
                    mode={mode}
                    myTeam={myTeam}
                    otherTeam={otherTeam}
                    players={players}
                    penalty={penalty}
                    questionIndex={questionIndex}
                    selectedProfileIds={selectedProfileIds}
                    socket={spectateSocket ?? socket}
                    timerLimitReached={timerLimitReached}
                  />
                )}
              </Stack>
            </Stack>
          </Paper>
          <Stack direction={'row'}>
            {!gameOver &&
            !customQuestionTestingData &&
            (!spectate || isAdmin) &&
            ((mode === 'solo' && !daily) || isAdmin) ? (
              <Tooltip title="F9 to pause">
                <Fab
                  variant="extended"
                  color="info"
                  size="small"
                  sx={{ margin: '8px' }}
                  onClick={handlePauseClick}
                >
                  <PauseIcon sx={{ mr: { xs: 0, md: 1 } }} />
                  <Box
                    component="span"
                    sx={{
                      display: { xs: 'none', md: 'inherit' },
                    }}
                  >
                    {willPause ? 'Unpause' : 'Pause'}
                  </Box>
                </Fab>
              </Tooltip>
            ) : null}
            {!gameOver &&
            !customQuestionTestingData &&
            (!spectate || isAdmin) &&
            ((mode === 'solo' && !!starterKit) || isAdmin) ? (
              <Fab
                variant="extended"
                size="small"
                sx={{ margin: '8px', color: theme.palette.demon.main }}
                onClick={abortStarterKitGame}
              >
                <StopIcon sx={{ mr: { xs: 0, md: 1 } }} />
                <Box
                  component="span"
                  sx={{
                    display: { xs: 'none', md: 'inherit' },
                  }}
                >
                  Abort [ESC]
                </Box>
              </Fab>
            ) : null}
            {!customQuestionTestingData ? (
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  marginLeft: 'auto',
                }}
              >
                <ReportInaccuracyButton
                  profileId={profileId}
                  questionIndex={questionIndex}
                  gameId={gameId}
                  socket={socket}
                ></ReportInaccuracyButton>
              </Box>
            ) : null}
          </Stack>
        </Box>
        {mode === 'sit-n-go' ||
        (variant === 'survival' && survivalMode === 'challenge') ? (
          <Drawer
            sx={{
              width: { xs: 144, sm: poolLeaderboardDrawerWidth },
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: { xs: 144, sm: poolLeaderboardDrawerWidth },
                boxSizing: 'border-box',
                bgcolor: '#333333',
                maxHeight: { xs: 416, sm: '100%' },
              },
            }}
            variant="permanent"
            anchor="right"
          >
            <Typography
              variant="h4"
              textAlign="center"
              letterSpacing={3}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              LEADERBOARD
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              letterSpacing={2}
              sx={{ display: { xs: 'block', sm: 'none' } }}
            >
              LEADERBOARD
            </Typography>
            <Typography
              variant={'caption'}
              textAlign={'center'}
              letterSpacing={3}
              display={'inline'}
              mt={1}
            >
              Total Players:
              <Typography
                variant={'subtitle1'}
                display={'inline'}
                fontWeight={600}
                ml={1}
              >
                {Object.values(players).length}
              </Typography>
            </Typography>
            {sortedPlayers ? (
              <PoolPlayLeaderboard
                cutoff={cutoff}
                gameOver={false}
                onUsernameClick={handlePoolLeaderboardUsernameClick}
                sortedPlayers={sortedPlayers}
                profileId={profileId}
                questionIndex={questionIndex}
                roundOver={roundOver}
                spectate={spectate}
              />
            ) : null}
          </Drawer>
        ) : null}
        {variant === 'survival' && survivalMode === 'challenge' ? (
          <Drawer
            sx={{
              width: { xs: 144, sm: poolLeaderboardDrawerWidth },
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: { xs: 144, sm: poolLeaderboardDrawerWidth },
                boxSizing: 'border-box',
                bgcolor: '#333333',
                maxHeight: { xs: 416, sm: '100%' },
                overflow: 'hidden',
              },
            }}
            variant="permanent"
            anchor="right"
          >
            <Stack
              height={'100%'}
              flex={1}
              alignItems={'center'}
              justifyContent={'center'}
              spacing={4}
              paddingX={2}
              marginY={2}
            >
              <Stack>
                <ChallengeLifes
                  lifes={lifes}
                  questionIndex={questionIndex}
                  showHeart={showHeart}
                  showUsedHeart={showUsedHeart}
                />
              </Stack>
              <Stack spacing={1}>
                <Typography
                  fontWeight={600}
                  textAlign={'center'}
                  sx={{
                    borderBottom: '1px solid',
                    padding: '5px',
                    marginBottom: 1,
                  }}
                >
                  Stage{' '}
                  {findMark(questionIndex + 1, challengeTowerStages).stage}{' '}
                  Challenges
                </Typography>
                {findMark(questionIndex + 1, challengeTowerStages)
                  .description ? (
                  findMark(questionIndex + 1, challengeTowerStages)
                    .challengeType === 'active' ? (
                    <Stack
                      direction={'row'}
                      spacing={1}
                      sx={{
                        border: '1px solid',
                        borderColor:
                          isChallengeMet() === 'yes'
                            ? theme.palette.underPar.main
                            : isChallengeMet() === 'no'
                            ? theme.palette.error.main
                            : '#2c93ba',
                        backgroundColor:
                          isChallengeMet() === 'yes'
                            ? theme.palette.underPar.dark
                            : isChallengeMet() === 'no'
                            ? theme.palette.error.dark
                            : '#001a30',
                        padding: 1,
                      }}
                    >
                      {isChallengeMet() === 'yes' ? (
                        <CheckCircleIcon color="success" />
                      ) : isChallengeMet() === 'no' ? (
                        <CloseIcon />
                      ) : (
                        <IndeterminateCheckBoxIcon color="info" />
                      )}
                      <Typography textAlign={'center'}>
                        {
                          findMark(questionIndex + 1, challengeTowerStages)
                            .description
                        }
                      </Typography>
                      {showUsedHeartOnChallenge === 'non-timeout' && (
                        <div className="disintegrate-animation">
                          <Stack
                            alignItems={'center'}
                            justifyContent={'center'}
                          >
                            <FavoriteIcon style={{ color: 'lightgray' }} />
                          </Stack>
                        </div>
                      )}
                    </Stack>
                  ) : (
                    <Typography
                      textAlign={'center'}
                      sx={{
                        color: theme.palette.dailies.main,
                      }}
                    >
                      {
                        findMark(questionIndex + 1, challengeTowerStages)
                          .description
                      }
                    </Typography>
                  )
                ) : null}
                <Stack
                  direction={'row'}
                  spacing={1}
                  sx={{
                    border: '1px solid',
                    borderColor: playerSelf?.correctAnswer
                      ? theme.palette.underPar.main
                      : playerSelf?.timerLimitReached
                      ? theme.palette.error.main
                      : '#2c93ba',
                    backgroundColor: playerSelf?.correctAnswer
                      ? theme.palette.underPar.dark
                      : playerSelf?.timerLimitReached
                      ? theme.palette.error.dark
                      : '#001a30',
                    padding: 1,
                  }}
                >
                  {playerSelf?.correctAnswer ? (
                    <CheckCircleIcon color="success" />
                  ) : playerSelf?.timerLimitReached ? (
                    <CloseIcon />
                  ) : (
                    <IndeterminateCheckBoxIcon color="info" />
                  )}
                  <Typography textAlign={'center'}>Do not timeout</Typography>
                  {showUsedHeartOnChallenge === 'timeout' && (
                    <div className="disintegrate-animation">
                      <Stack alignItems={'center'} justifyContent={'center'}>
                        <FavoriteIcon style={{ color: 'lightgray' }} />
                      </Stack>
                    </div>
                  )}
                </Stack>
              </Stack>
              <Box height={'70%'}>
                <ChallengeTowerBar
                  questionIndex={questionIndex}
                  marks={challengeTowerStages}
                />
              </Box>
            </Stack>
          </Drawer>
        ) : null}
      </Box>
    </>
  );
};
